import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { T, MONO } from './poyais-ui/theme';
import { Icon } from './poyais-ui/icons';
import { Button } from './poyais-ui/primitives';
import { useLang, addTranslations } from './i18n';
import PoyaisSpinner from './PoyaisSpinner';

addTranslations({
  'Bu board yüklenemedi.': 'Could not load this board.',
  'Kart oluşturulamadı.': 'Could not create card.',
  'Bu Trello board bağlantısı kaldırılsın mı? İçe aktarılmış görevleri siler (Trello tarafındaki kartlar etkilenmez).': 'Unlink this Trello board? This deletes the imported tasks (cards on Trello itself are not affected).',
  'Bağlantı kaldırılamadı.': 'Could not remove the connection.',
  "Trello'da Aç": 'Open in Trello',
  'Kaldırılıyor…': 'Removing…',
  'Bağlantıyı Kaldır': 'Remove Connection',
  'Yükleniyor…': 'Loading…',
  'Kart Ekle': 'Add Card',
  "Bu board'da liste bulunamadı.": 'No lists found in this board.',
  'Trello Kartı': 'Trello Card',
  'Açıklama': 'Description',
  'Kart detayı ekle…': 'Add card details…',
  'Liste': 'List',
  'Değişiklikler otomatik olarak Trello kartına da yansır. Kontrol listesi, yorum ve dosya ekleri için görevi Görevler sayfasından da açabilirsin.': 'Changes are automatically reflected on the Trello card too. For checklists, comments, and attachments, you can also open the task from the Tasks page.',
  'Kartı Sil': 'Delete Card',
  'Yeni kart': 'New card',
});

const API = '/api/v1';

type TrelloList = { id: number; board_row_id: number; trello_list_id: string; name: string; position: number };
type TrelloTask = {
  id: number; title: string; description?: string; trello_list_row_id: number | null;
  trello_card_id: string | null;
};

export default function TrelloBoardDetail({ boardRowId, boardName, trelloBoardId, clientId, onBack }: {
  boardRowId: number; boardName: string; trelloBoardId: string; clientId: number; onBack: () => void;
}) {
  const { t } = useLang();
  const [lists, setLists] = useState<TrelloList[]>([]);
  const [tasks, setTasks] = useState<TrelloTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<TrelloTask | null>(null);
  const [dragOverList, setDragOverList] = useState<number | null>(null);
  const [unlinking, setUnlinking] = useState(false);
  const trelloUrl = `https://trello.com/b/${trelloBoardId}`;

  const load = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/trello/board/${boardRowId}/lists`, { params: { client_id: clientId } }),
      axios.get(`${API}/trello/board/${boardRowId}/tasks`, { params: { client_id: clientId } }),
    ])
      .then(([lr, tr]) => { setLists(lr.data.sort((a: TrelloList, b: TrelloList) => a.position - b.position)); setTasks(tr.data); })
      .catch(() => setError(t('Bu board yüklenemedi.')))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [boardRowId, clientId]);

  const addCard = (listRowId: number) => {
    axios.post(`${API}/trello/board-task`, { client_id: clientId, board_row_id: boardRowId, list_row_id: listRowId, title: t('Yeni kart') })
      .then(r => setTasks(ts => [...ts, r.data])).catch(() => alert(t('Kart oluşturulamadı.')));
  };

  const moveCard = (taskId: number, listRowId: number) => {
    setTasks(ts => ts.map(t => t.id === taskId ? { ...t, trello_list_row_id: listRowId } : t));
    axios.patch(`${API}/trello/board-task/${taskId}`, { client_id: clientId, list_row_id: listRowId }).catch(() => load());
  };

  const saveCard = (taskId: number, patch: Partial<TrelloTask>) => {
    setTasks(ts => ts.map(t => t.id === taskId ? { ...t, ...patch } : t));
    setEditing(e => e && e.id === taskId ? { ...e, ...patch } : e);
    axios.patch(`${API}/trello/board-task/${taskId}`, { client_id: clientId, ...patch }).catch(() => load());
  };

  const deleteCard = (taskId: number) => {
    setTasks(ts => ts.filter(t => t.id !== taskId));
    setEditing(null);
    axios.delete(`${API}/trello/board-task/${taskId}`, { params: { client_id: clientId } }).catch(() => load());
  };

  const unlink = () => {
    if (!window.confirm(t('Bu Trello board bağlantısı kaldırılsın mı? İçe aktarılmış görevleri siler (Trello tarafındaki kartlar etkilenmez).'))) return;
    setUnlinking(true);
    axios.post(`${API}/trello/unlink-board/${boardRowId}`, null, { params: { client_id: clientId } })
      .then(() => onBack()).catch(() => alert(t('Bağlantı kaldırılamadı.'))).finally(() => setUnlinking(false));
  };

  if (error) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <button onClick={onBack} style={{ width: 32, height: 32, borderRadius: 9, border: `1px solid ${T.border}`, background: T.surface, cursor: 'pointer', color: T.textMid, fontSize: 15 }}>←</button>
        <div style={{ fontSize: 13, color: T.redText }}>{error}</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' as const, gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={onBack} style={{ width: 32, height: 32, borderRadius: 9, border: `1px solid ${T.border}`, background: T.surface, cursor: 'pointer', color: T.textMid, fontSize: 15, flexShrink: 0 }}>←</button>
          <span style={{ width: 30, height: 30, borderRadius: 9, background: 'var(--py-blue-tint)', color: '#0052CC', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon.trello size={16} color="#0052CC" />
          </span>
          <div style={{ fontSize: 19, fontWeight: 800, letterSpacing: '-0.02em', color: T.text }}>{boardName}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <a href={trelloUrl} target="_blank" rel="noreferrer"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: '#0052CC', textDecoration: 'none' }}>
            {t("Trello'da Aç")} <Icon.external size={12} sw={2} color="#0052CC" />
          </a>
          <Button variant="ghost" onClick={unlink} disabled={unlinking}>{unlinking ? t('Kaldırılıyor…') : t('Bağlantıyı Kaldır')}</Button>
        </div>
      </div>

      {loading ? (
        <PoyaisSpinner size={36} pad={24}/>
      ) : (
        <div style={{ display: 'flex', gap: 14, overflowX: 'auto' as const, paddingBottom: 6, alignItems: 'flex-start' }}>
          {lists.map(list => {
            const listTasks = tasks.filter(tk => tk.trello_list_row_id === list.id);
            return (
              <div key={list.id}
                onDragOver={e => { e.preventDefault(); setDragOverList(list.id); }}
                onDragLeave={() => setDragOverList(c => c === list.id ? null : c)}
                onDrop={e => {
                  e.preventDefault(); setDragOverList(null);
                  const id = Number(e.dataTransfer.getData('text/trello-task-id'));
                  if (id) moveCard(id, list.id);
                }}
                style={{ width: 264, flexShrink: 0, borderRadius: 12, background: dragOverList === list.id ? 'var(--py-track-bg)' : 'transparent', padding: dragOverList === list.id ? 6 : 0, transition: 'background .12s' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 12, padding: '0 2px' }}>
                  <span style={{ fontSize: 13.5, fontWeight: 800, color: T.text }}>{list.name}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--py-text-mid-alt)', fontFamily: MONO }}>{listTasks.length}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {listTasks.map(tk => (
                    <div key={tk.id} draggable
                      onDragStart={e => e.dataTransfer.setData('text/trello-task-id', String(tk.id))}
                      onClick={() => setEditing(tk)}
                      style={{ border: `1px solid ${T.border}`, borderRadius: 13, background: T.surface, padding: '12px 14px', cursor: 'pointer' }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: T.text, lineHeight: 1.4 }}>{tk.title}</div>
                    </div>
                  ))}
                  <div onClick={() => addCard(list.id)}
                    style={{ border: '1px dashed var(--py-border-soft)', borderRadius: 11, padding: '10px 12px', textAlign: 'center' as const, fontSize: 12, fontWeight: 700, color: T.textDim, cursor: 'pointer' }}>
                    {t('Kart Ekle')}
                  </div>
                </div>
              </div>
            );
          })}
          {lists.length === 0 && <div style={{ fontSize: 13, color: T.textDim }}>{t("Bu board'da liste bulunamadı.")}</div>}
        </div>
      )}

      {editing && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999 }}>
          <div onClick={() => setEditing(null)} style={{ position: 'absolute', inset: 0, background: 'rgba(20,22,43,.35)' }} />
          <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 420, maxWidth: '92vw', background: T.surface,
            boxShadow: '-8px 0 32px rgba(20,22,43,.18)', display: 'flex', flexDirection: 'column', padding: '22px 26px', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 11, fontWeight: 700, color: '#0052CC' }}>
                <Icon.trello size={13} color="#0052CC" />{t('Trello Kartı')}
              </span>
              <button onClick={() => setEditing(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: T.textDim, lineHeight: 1 }}>×</button>
            </div>
            <input value={editing.title}
              onChange={e => setEditing(prev => prev ? { ...prev, title: e.target.value } : prev)}
              onBlur={() => saveCard(editing.id, { title: editing.title })}
              style={{ width: '100%', border: 'none', outline: 'none', fontSize: 17, fontWeight: 800, fontFamily: 'inherit', color: T.text, padding: 0 }} />
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.textDim, marginBottom: 8, textTransform: 'uppercase' as const, letterSpacing: '.06em' }}>{t('Açıklama')}</div>
              <textarea value={editing.description || ''}
                onChange={e => setEditing(prev => prev ? { ...prev, description: e.target.value } : prev)}
                onBlur={() => saveCard(editing.id, { description: editing.description })}
                rows={5} placeholder={t('Kart detayı ekle…')}
                style={{ width: '100%', border: `1px solid ${T.borderMid}`, borderRadius: 10, padding: '10px 12px', fontSize: 13, fontFamily: 'inherit', outline: 'none', resize: 'vertical' as const, boxSizing: 'border-box' as const, background: 'var(--py-subtle-bg)' }} />
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.textDim, marginBottom: 8, textTransform: 'uppercase' as const, letterSpacing: '.06em' }}>{t('Liste')}</div>
              <select value={editing.trello_list_row_id ?? ''} onChange={e => saveCard(editing.id, { trello_list_row_id: Number(e.target.value) })}
                style={{ width: '100%', border: `1px solid ${T.borderMid}`, borderRadius: 10, padding: '9px 12px', fontSize: 13, fontFamily: 'inherit', outline: 'none', background: T.surface, boxSizing: 'border-box' as const }}>
                {lists.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            <div style={{ fontSize: 11.5, color: T.textFaint, lineHeight: 1.5 }}>
              {t('Değişiklikler otomatik olarak Trello kartına da yansır. Kontrol listesi, yorum ve dosya ekleri için görevi Görevler sayfasından da açabilirsin.')}
            </div>
            <div style={{ marginTop: 'auto', paddingTop: 16 }}>
              <button onClick={() => deleteCard(editing.id)}
                style={{ width: '100%', padding: '10px 16px', borderRadius: 10, border: `1px solid ${T.border}`, background: T.surface, color: T.redText, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>{t('Kartı Sil')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
